/* eslint-disable react/prop-types */

import { debounce } from 'lodash';
import moment from 'moment-timezone';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { getReportData, getTotalCount } from '../../../../../api/reports';
import { Loader, Pagination, Table } from '../../../../../common/components';
import { useSetLoading } from '../../../../../common/hooks/appearance';
import { usePagination } from '../../../../../common/hooks/data';
import { fetchDashboardFilterData, reports } from '../../../../../utils/constant';
import * as handlers from '../../../../../utils/handlers';
import DownloadModal from '../../../downloadModal/downloadModal';
import EmailModal from '../../../emailModal/emailModal';
import ItemEmptyTile from '../../../item-empty-tile/item-empty-tile';
import Toolbar from '../../../toolbar/toolbar';
import onDownloadClick from '../../onDownloadClickHandler';
import InterestAndFinancebyCustomerReportTableRow from './interestAndFinancebyCustomerReportTableRow';

const InterestAndFinancebyCustomerReportTable = ({
  tableData,
  totalResults,
  fromDate,
  toDate,
  dynamicHeaders,
}) => {
  const filterData = useSelector((state) => state.filters.dashboardMultiSelectFilters);

  const [interestAndFinanceTableData, setInterestAndFinanceTableData] = useState([]);
  const { page, limit, setPage, setLimit } = usePagination();
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [loading, setLoading] = useState(false);
  const [applyFilters, setApplyFilters] = useState(false);
  const [slStartNumber, setSlStartNumber] = useState(null);
  const [totalCount, setTotalCount] = useState(null);
  const [tempDynamicHeaders, setTempDynamicHeaders] = useState([]);
  const [initialRenderDone, setInitialRenderDone] = useState(false);

  useEffect(() => {
    if (!initialRenderDone) {
      setInitialRenderDone(true);
      return;
    }
    getUpdatedTableData('globalFilterChange');
  }, [filterData]);

  const toggleEmailModal = () => {
    if (interestAndFinanceTableData?.length) {
      setIsEmailModalOpen(!isEmailModalOpen);
    }
  };

  const toggleDownloadModal = () => {
    if (interestAndFinanceTableData?.length) {
      setIsDownloadModalOpen(!isDownloadModalOpen);
    }
  };

  const tableColumns = [
    'SL.NO.',
    'PURCHASER NAME',
    'AGREEMENT TYPE',
    'OPENING BALANCE',
    ...tempDynamicHeaders,
    'FUTURE BALANCE',
    'TOTAL BALANCE',
    'SCHEDULE PIF',
  ];

  useEffect(() => {
    setSlStartNumber(page * limit - limit);
    if (applyFilters) {
      getUpdatedTableData('pagination');
    }
  }, [page, limit, applyFilters]);

  const getUpdatedTableData = async (type = 'pagination', value = '') => {
    setLoading(true);
    setApplyFilters(false);

    let page2 = page;
    let limit2 = limit;
    if (type === 'search' || type === 'globalFilterChange') {
      page2 = 1;
      limit2 = 5;
    }
    try {
      const requestParams = {
        ...fetchDashboardFilterData(filterData),
        startDate: fromDate,
        endDate: toDate,
        page: page2,
        limit: limit2,
        timezone: moment.tz.guess(),
        ...((type === 'search' || searchText.length > 0) && {
          opiOrName: type === 'search' ? value : searchText,
        }),
      };

      const { data } = await getReportData(reports.INTEREST_AND_FINANCE_BY_CUSTOMER, requestParams);
      if (type === 'search' || type === 'globalFilterChange') {
        setPage(1);
        setLimit(5);
        const response = await getTotalCount(reports.INTEREST_AND_FINANCE_BY_CUSTOMER, {
          ...fetchDashboardFilterData(filterData),
          startDate: fromDate,
          endDate: toDate,
          opiOrName: type === 'search' ? value : searchText,
          timezone: moment.tz.guess(),
        });
        setTotalCount(response?.data?.data?.totalCount);
      }

      setInterestAndFinanceTableData(data?.data?.items || []);
      setTempDynamicHeaders(data?.data?.allTimePeriods || []);

      setLoading(false);
    } catch (error) {
      setLoading(false);
      handlers.apiErrorHandler(error);
    }
  };

  const debounceApiCall = useCallback(
    debounce((val) => {
      getUpdatedTableData('search', val);
    }, 2000),
    [],
  );

  const onSerchInputChange = (event) => {
    const {
      target: { value },
    } = event;
    setSearchText(value);
    debounceApiCall(value);
  };

  useEffect(() => {
    setInterestAndFinanceTableData(tableData);
    setTotalCount(totalResults);
    setPage(1);
    setLimit(5);
    setSearchText('');
    setTempDynamicHeaders(dynamicHeaders);
  }, [tableData, setLimit, setPage, totalResults, dynamicHeaders]);

  const LoaderComponent = () => {
    if (loading) {
      return <Loader />;
    }
    return <ItemEmptyTile />;
  };
  const showLoading = useMemo(() => loading, [loading]);
  useSetLoading(showLoading);

  return (
    <>
      <Toolbar
        title="Interest & Finance by Customer"
        optionOneLabel="Email Report"
        optionOneIcon="MdOutlineEmail"
        onOptionOneClick={toggleEmailModal}
        optionTwoLabel="Download Report"
        optionTwoIcon="FiDownload"
        onOptionTwoClick={toggleDownloadModal}
        searchText={searchText}
        onSerchInputChange={onSerchInputChange}
        isSearchable
        SearchInputPlaceholder="Search Using OPI Number or Contract Number"
        isTableDataPresent={interestAndFinanceTableData?.length > 0}
      />
      {interestAndFinanceTableData && interestAndFinanceTableData?.length > 0 ? (
        <>
          <div className="table-resp-scroll">
            <Table headers={tableColumns} bordered responsive className="dashboard-table">
              <tbody>
                {interestAndFinanceTableData?.map((rowData, rowNumber) => (
                  <InterestAndFinancebyCustomerReportTableRow
                    key={slStartNumber + rowNumber + 1}
                    rowData={rowData}
                    id={slStartNumber + rowNumber + 1}
                    dynamicHeaders={tempDynamicHeaders}
                  />
                ))}
              </tbody>
            </Table>
            {loading && <Loader />}
          </div>
          <div className="pagination-araging-report">
            <Pagination
              totalResults={totalCount}
              page={page}
              limit={limit}
              setPage={(pageNo) => {
                setApplyFilters(true);
                setPage(pageNo);
              }}
              setLimit={(rowlimit) => {
                setApplyFilters(true);
                setLimit(rowlimit);
              }}
            />
          </div>
        </>
      ) : (
        <div className="loader-empty">
          <LoaderComponent />
        </div>
      )}

      <EmailModal
        dateRangeDiffGreaterthanYear={moment(toDate).diff(moment(fromDate), 'days') > 365}
        isOpen={isEmailModalOpen}
        toggle={toggleEmailModal}
        reportType={reports.INTEREST_AND_FINANCE_BY_CUSTOMER}
        apiValues={{
          startDate: fromDate,
          endDate: toDate,
          ...(searchText && { opiOrName: searchText }),
        }}
        newFilters
      />
      <DownloadModal
        dateRangeDiffGreaterthanYear={moment(toDate).diff(moment(fromDate), 'days') > 365}
        isOpen={isDownloadModalOpen}
        toggle={toggleDownloadModal}
        reportType={reports.INTEREST_AND_FINANCE_BY_CUSTOMER}
        urlParams={{
          startDate: fromDate,
          endDate: toDate,
          action: 'download',
          ...(searchText && { opiOrName: searchText }),
        }}
        onDownloadClick={onDownloadClick}
        willReceiveReportFileInEmail
        newFilters
      />
    </>
  );
};

export default InterestAndFinancebyCustomerReportTable;
